import { useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useUploadDocument } from '../../hooks/backend/documents'
import { writeErrorMessage } from '../../utils/writeError'

const MAX_BYTES = 15 * 1024 * 1024

export type UploadCategory = { code: string; label: string }

// Reads a File as base64, stripping the "data:...;base64," prefix.
function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      resolve(result.split(',')[1] ?? '')
    }
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'))
    reader.readAsDataURL(file)
  })
}

// Upload control shared by the vehicle and owner document dialogs. Renders a
// category picker, a file input, and an Upload button. On submit it reads the
// file to base64 and calls the upload backend function with the dialog's scope.
export function UploadControl({
  categories,
  scope,
  onUploaded,
}: {
  categories: UploadCategory[]
  scope: { vehicle_id?: string; owner_person_id?: string }
  onUploaded: () => void
}) {
  const upload = useUploadDocument()
  const inputRef = useRef<HTMLInputElement>(null)
  const [category, setCategory] = useState(categories[0]?.code ?? '')
  const [file, setFile] = useState<File | null>(null)
  const [tooLarge, setTooLarge] = useState(false)

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.files?.[0] ?? null
    setFile(next)
    setTooLarge(next != null && next.size > MAX_BYTES)
  }

  function reset() {
    setFile(null)
    setTooLarge(false)
    if (inputRef.current) inputRef.current.value = ''
  }

  async function handleUpload() {
    if (!file || tooLarge) return
    try {
      const contentBase64 = await readAsBase64(file)
      await upload.trigger({
        vehicle_id: scope.vehicle_id ?? null,
        owner_person_id: scope.owner_person_id ?? null,
        category,
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        contentBase64,
      }).result
      toast.success('Document uploaded')
      reset()
      onUploaded()
    } catch (err) {
      toast.error('Could not upload document', { description: writeErrorMessage(err) })
    }
  }

  const uploading = upload.loading

  return (
    <div className="mb-4 space-y-2 rounded-md border border-border bg-muted/30 p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="space-y-1.5 sm:w-56">
          <Label htmlFor="doc-category" className="text-xs">
            Category
          </Label>
          <NativeSelect
            id="doc-category"
            value={category}
            disabled={uploading}
            onChange={(e) => setCategory(e.target.value)}
            className="h-9"
          >
            {categories.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </NativeSelect>
        </div>

        <div className="min-w-0 flex-1 space-y-1.5">
          <Label htmlFor="doc-file" className="text-xs">
            File
          </Label>
          <input
            ref={inputRef}
            id="doc-file"
            type="file"
            disabled={uploading}
            onChange={handleFileChange}
            className="flex h-9 w-full rounded-md border border-input bg-background text-sm text-foreground ring-offset-background file:mr-3 file:h-full file:border-0 file:bg-muted file:px-3 file:text-sm file:font-medium file:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        <Button
          onClick={handleUpload}
          disabled={!file || tooLarge || uploading}
          size="sm"
          className="h-9 shrink-0 gap-1.5"
        >
          {uploading ? <Spinner className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
          Upload
        </Button>
      </div>

      {tooLarge && (
        <p className="text-xs text-destructive">
          This file is larger than 15 MB. Please choose a smaller file.
        </p>
      )}
    </div>
  )
}
